import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClientsContext } from "@/App";
import {
  Search, Target, Loader2, CheckCircle2, X, Calendar, Package,
  Upload, Trash2, AlertTriangle, ChevronDown, ChevronUp, Info, Hash, ListChecks,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const STAGE_COLORS = {
  "PO Received": "#3b82f6", "Procurement": "#f97316", "Manufacturing": "#a855f7",
  "Quality Control": "#14b8a6", "Dispatch": "#f59e0b", "Completed": "#94a3b8",
};

function parseDateDash(s) {
  if (!s || typeof s !== "string") return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function formatCurrency(val) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

function normStr(s) {
  return (s || "").toString().trim().toLowerCase();
}

function normLineNum(s) {
  // Strip leading zeros for comparison: "001" → "1"
  const n = normStr(s).replace(/^0+/, "");
  return n || "0";
}

/**
 * Parse a CSV text and find matching items from the allItems array.
 * Expected columns (case-insensitive, any order): po_number, line_number, part_number
 * Returns { matchedIds, matchedRows, unmatchedRows, total }
 */
function parseCsvForTarget(csvText, allItems) {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) return { matchedIds: new Set(), matchedRows: [], unmatchedRows: [], total: 0 };

  const rawHeaders = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, "").toLowerCase());

  const idx = (keywords) => rawHeaders.findIndex(h => keywords.some(k => h.includes(k)));
  const poIdx   = idx(["po_number", "po number", "po#", "po "]);
  const lineIdx = idx(["line_number", "line number", "line#", "line_no", "lineno"]);
  const partIdx = idx(["part_number", "part number", "part#", "part_no", "partno"]);

  const dataRows = lines.slice(1).filter(l => l.trim());
  const matchedIds = new Set();
  const matchedRows = [];
  const unmatchedRows = [];

  for (const row of dataRows) {
    const cells = row.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
    const csvPo   = poIdx >= 0   ? normStr(cells[poIdx])         : "";
    const csvLine = lineIdx >= 0 ? normLineNum(cells[lineIdx])   : null;
    const csvPart = partIdx >= 0 ? normStr(cells[partIdx])       : null;

    if (!csvPo) { unmatchedRows.push({ po: cells[0] || "—", line: "—", part: "—", reason: "Empty PO number" }); continue; }

    const match = allItems.find(item => {
      const itemPo   = normStr(item.po_number);
      if (itemPo !== csvPo) return false;
      if (csvLine !== null && normLineNum(item.line_number) === csvLine) return true;
      if (csvPart && normStr(item.part_number) === csvPart) return true;
      return false;
    });

    if (match) {
      if (!matchedIds.has(match.id)) {
        matchedIds.add(match.id);
        matchedRows.push({
          csvPo: cells[poIdx] || "",
          csvLine: cells[lineIdx] || "",
          csvPart: cells[partIdx] || "",
          id: match.id,
          desc: match.item_description || "",
          part: match.part_number || match.po_number || "",
        });
      }
    } else {
      unmatchedRows.push({
        po: cells[poIdx] || "—",
        line: cells[lineIdx] || "—",
        part: cells[partIdx] || "—",
        reason: "No matching item found",
      });
    }
  }

  return { matchedIds, matchedRows, unmatchedRows, total: dataRows.length };
}

/**
 * MonthlyTargetModal — Admin/target-setter UI to curate which line items count
 * toward a month's commitment.
 *
 * Props:
 *  open, onOpenChange, items (full line-items array), showValue,
 *  onSaved(year, month), initialYear, initialMonth
 */
export default function MonthlyTargetModal({ open, onOpenChange, items, showValue, onSaved, initialYear, initialMonth }) {
  const { clients: clientList } = useContext(ClientsContext);
  const today = useMemo(() => new Date(), []);
  const [year, setYear]       = useState(initialYear  ?? today.getFullYear());
  const [month, setMonth]     = useState(initialMonth ?? today.getMonth() + 1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch]   = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [dueFilter, setDueFilter]     = useState("target-month");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [setBy, setSetBy]     = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null); // { matchedRows, unmatchedRows, total, matchedIds }
  const [showUnmatched, setShowUnmatched] = useState(false);
  const csvInputRef = useRef(null);
  const [targetType, setTargetType] = useState("po_lines"); // "po_lines" | "race"
  const [raceTarget, setRaceTarget] = useState(100);

  // Fetch existing target whenever month/year changes (or modal opens)
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setCsvPreview(null);
    setConfirmReset(false);
    const token = localStorage.getItem("rl_token");
    axios.get(`${BACKEND_URL}/api/monthly-targets/${year}/${month}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => {
        setSelectedIds(new Set(r.data.item_ids || []));
        setSetBy(r.data.set_by || null);
        setUpdatedAt(r.data.updated_at || null);
        setTargetType(r.data.target_type || "po_lines");
        setRaceTarget(r.data.race_target || 100);
      })
      .catch(() => { setSelectedIds(new Set()); setSetBy(null); setUpdatedAt(null); })
      .finally(() => setLoading(false));
  }, [open, year, month]);

  const yearOptions = useMemo(() => {
    const cy = today.getFullYear();
    return [cy - 1, cy, cy + 1, cy + 2];
  }, [today]);

  // Candidate set filtered by dueFilter; items already in target always kept visible
  const candidateItems = useMemo(() => {
    const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
    const inTargetMonth = (d) => {
      const s = new Date(year, month - 1, 1);
      const e = new Date(year, month, 0, 23, 59, 59);
      return d >= s && d <= e;
    };
    const inThisMonth = (d) => {
      const s = new Date(todayMid.getFullYear(), todayMid.getMonth(), 1);
      const e = new Date(todayMid.getFullYear(), todayMid.getMonth() + 1, 0, 23, 59, 59);
      return d >= s && d <= e;
    };
    const inThisQuarter = (d) => {
      const q = Math.floor(todayMid.getMonth() / 3);
      const s = new Date(todayMid.getFullYear(), q * 3, 1);
      const e = new Date(todayMid.getFullYear(), q * 3 + 3, 0, 23, 59, 59);
      return d >= s && d <= e;
    };
    const withinDays = (d, n) => {
      const upper = new Date(todayMid); upper.setDate(upper.getDate() + n);
      return d >= todayMid && d <= upper;
    };
    return items.filter(i => {
      if (selectedIds.has(i.id)) return true;
      if (dueFilter === "all") return true;
      const d = parseDateDash(i.dispatch_due_date || i.due_date);
      if (!d) return false;
      d.setHours(0, 0, 0, 0);
      switch (dueFilter) {
        case "target-month": return inTargetMonth(d);
        case "this-month":   return inThisMonth(d);
        case "this-quarter": return inThisQuarter(d);
        case "next-30":      return withinDays(d, 30);
        case "next-60":      return withinDays(d, 60);
        case "next-90":      return withinDays(d, 90);
        case "overdue":      return d < todayMid && i.stage !== "Completed";
        default:             return true;
      }
    });
  }, [items, dueFilter, year, month, selectedIds]);

  const visibleItems = useMemo(() => {
    let f = candidateItems;
    if (stageFilter !== "all") f = f.filter(i => i.stage === stageFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      f = f.filter(i =>
        (i.po_number      || "").toLowerCase().includes(q) ||
        (i.item_description || "").toLowerCase().includes(q) ||
        (i.client         || "").toLowerCase().includes(q) ||
        (i.vendor         || "").toLowerCase().includes(q) ||
        (i.part_number    || "").toLowerCase().includes(q) ||
        (i.line_number    || "").toLowerCase().includes(q)
      );
    }
    return [...f].sort((a, b) => {
      const sa = selectedIds.has(a.id) ? 0 : 1;
      const sb = selectedIds.has(b.id) ? 0 : 1;
      if (sa !== sb) return sa - sb;
      const da = parseDateDash(a.dispatch_due_date || a.due_date);
      const db = parseDateDash(b.dispatch_due_date || b.due_date);
      return (da?.getTime() || 0) - (db?.getTime() || 0);
    });
  }, [candidateItems, search, stageFilter, selectedIds]);

  const toggle = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectAllVisible = () => {
    setSelectedIds(prev => { const n = new Set(prev); visibleItems.forEach(i => n.add(i.id)); return n; });
  };
  const clearAllVisible = () => {
    setSelectedIds(prev => { const n = new Set(prev); visibleItems.forEach(i => n.delete(i.id)); return n; });
  };

  // CSV import handler — shows preview before adding to selection
  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const result = parseCsvForTarget(text, items);
      if (result.total === 0) {
        toast.error("CSV appears empty or has no data rows.");
        return;
      }
      setShowUnmatched(false);
      setCsvPreview({ ...result });
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const confirmCsvImport = () => {
    if (!csvPreview) return;
    setSelectedIds(prev => { const n = new Set(prev); csvPreview.matchedIds.forEach(id => n.add(id)); return n; });
    toast.success(`Added ${csvPreview.matchedRows.length} items from CSV`, {
      description: csvPreview.unmatchedRows.length > 0
        ? `${csvPreview.unmatchedRows.length} row(s) unmatched — check the preview for details.`
        : "All CSV rows matched successfully.",
    });
    setCsvPreview(null);
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const token = localStorage.getItem("rl_token");
      await axios.put(
        `${BACKEND_URL}/api/monthly-targets/${year}/${month}`,
        { item_ids: [], target_type: "po_lines", race_target: null },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setSelectedIds(new Set());
      setSetBy(null);
      setUpdatedAt(null);
      setConfirmReset(false);
      setTargetType("po_lines");
      setRaceTarget(100);
      toast.success(`Target cleared for ${MONTHS_LONG[month - 1]} ${year}`);
      onSaved && onSaved(year, month);
    } catch (e) {
      toast.error("Failed to reset target", { description: e?.response?.data?.detail || e.message });
    } finally {
      setResetting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("rl_token");
      const item_ids = targetType === "po_lines" ? Array.from(selectedIds) : [];
      const payload = {
        item_ids,
        target_type: targetType,
        race_target: targetType === "race" ? (Number(raceTarget) || 0) : null,
      };
      await axios.put(
        `${BACKEND_URL}/api/monthly-targets/${year}/${month}`,
        payload,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(`Target saved for ${MONTHS_LONG[month - 1]} ${year}`, {
        description: targetType === "race"
          ? `Race to ${raceTarget} dispatched items`
          : `${item_ids.length} PO line${item_ids.length === 1 ? "" : "s"} in target`,
      });
      onSaved && onSaved(year, month);
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to save target", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const clientColor = (name) => clientList.find(c => c.name === name)?.color || "#94a3b8";
  const totalValue = useMemo(
    () => Array.from(selectedIds).reduce((s, id) => s + (items.find(i => i.id === id)?.value_usd || 0), 0),
    [selectedIds, items]
  );

  // Shared grid columns — with or without value column
  const gridCols = showValue
    ? "36px 100px 52px 88px 1fr 108px 46px 64px 76px"
    : "36px 110px 52px 95px 1fr 108px 46px 64px";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[97vw] max-h-[92vh] flex flex-col p-0 overflow-hidden" data-testid="monthly-target-modal">

        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-[hsl(var(--border))] shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-lg bg-orange-500/10 shrink-0">
                <Target className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold tracking-tight">Monthly Target</DialogTitle>
                <DialogDescription className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                  Select the PO lines that count toward this month's dispatch commitment. The dashboard tracks dispatched-of-target progress.
                </DialogDescription>
                {setBy && updatedAt && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    <span>Last set by <span className="font-semibold text-[hsl(var(--foreground))]">{setBy}</span> · {new Date(updatedAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* ── Target month selector + mode toggle ── */}
        <div className="px-6 py-2.5 border-b border-[hsl(var(--border))] bg-orange-500/[0.05] flex flex-wrap items-center gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-orange-500/80" />
            <span className="text-xs font-bold uppercase tracking-widest text-orange-700 dark:text-orange-400">
              Set target for
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-8 w-[140px] text-xs font-bold border-orange-500/40 bg-[hsl(var(--background))] text-orange-700 dark:text-orange-400 focus:ring-1 focus:ring-orange-500/40" data-testid="target-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS_LONG.map((m, idx) => (
                  <SelectItem key={m} value={String(idx + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-8 w-[92px] text-xs font-bold border-orange-500/40 bg-[hsl(var(--background))] text-orange-700 dark:text-orange-400 focus:ring-1 focus:ring-orange-500/40" data-testid="target-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ── Mode slide-switch ── */}
          <div
            className="ml-auto flex items-center p-0.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40"
            data-testid="target-mode-switch"
          >
            <button
              type="button"
              onClick={() => setTargetType("po_lines")}
              data-testid="target-mode-po-lines"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all duration-200 ${
                targetType === "po_lines"
                  ? "bg-orange-500 text-white shadow-sm"
                  : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <ListChecks className="w-3 h-3" />
              PO Line Wise
            </button>
            <button
              type="button"
              onClick={() => setTargetType("race")}
              data-testid="target-mode-race"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all duration-200 ${
                targetType === "race"
                  ? "bg-orange-500 text-white shadow-sm"
                  : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <Hash className="w-3 h-3" />
              Race to a Number
            </button>
          </div>
        </div>

        {/* ── Race-to-a-Number panel (shown only in race mode) ── */}
        {targetType === "race" ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 py-10 min-h-[320px]" data-testid="race-panel">
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center mb-1">
                <Hash className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="text-base font-bold text-[hsl(var(--foreground))]">Race to a Number</h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))] max-w-sm leading-relaxed">
                Set a dispatch count target for {MONTHS_LONG[month - 1]} {year}. Any item shipped
                or completed this month counts toward the goal — regardless of PO or client.
              </p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">
                Target count
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setRaceTarget(t => Math.max(1, (Number(t) || 0) - 10))}
                  className="w-9 h-9 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 hover:bg-[hsl(var(--muted))]/80 text-lg font-bold transition-colors flex items-center justify-center"
                >−</button>
                <input
                  type="number"
                  min={1}
                  max={9999}
                  value={raceTarget}
                  onChange={e => setRaceTarget(Math.max(1, Number(e.target.value) || 1))}
                  className="w-28 text-center text-3xl font-bold font-mono-data border-2 border-orange-500/50 rounded-xl py-2 bg-[hsl(var(--background))] outline-none focus:border-orange-500 transition-colors text-orange-600 dark:text-orange-400"
                  data-testid="race-target-input"
                />
                <button
                  type="button"
                  onClick={() => setRaceTarget(t => Math.min(9999, (Number(t) || 0) + 10))}
                  className="w-9 h-9 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 hover:bg-[hsl(var(--muted))]/80 text-lg font-bold transition-colors flex items-center justify-center"
                >+</button>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                items dispatched in {MONTHS_LONG[month - 1]} {year}
              </p>
            </div>
          </div>
        ) : (
          <>
        {/* ── Discovery filters + CSV import ── */}
        <div className="px-4 py-2.5 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 flex flex-wrap items-center gap-2 shrink-0">
          {/* Search */}
          <div className="relative flex-1 min-w-[160px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PO #, part #, line #, client…"
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:outline-none focus:ring-1 focus:ring-orange-500"
              data-testid="target-search-input"
            />
          </div>

          <Select value={dueFilter} onValueChange={setDueFilter}>
            <SelectTrigger className="h-8 w-[168px] text-xs" data-testid="target-due-filter">
              <Calendar className="w-3 h-3 mr-1 opacity-60" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="target-month">Due in target month</SelectItem>
              <SelectItem value="all">All due dates</SelectItem>
              <SelectItem value="this-month">Due this month</SelectItem>
              <SelectItem value="this-quarter">Due this quarter</SelectItem>
              <SelectItem value="next-30">Due in next 30 days</SelectItem>
              <SelectItem value="next-60">Due in next 60 days</SelectItem>
              <SelectItem value="next-90">Due in next 90 days</SelectItem>
              <SelectItem value="overdue">Overdue (active only)</SelectItem>
            </SelectContent>
          </Select>

          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="target-stage-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {Object.keys(STAGE_COLORS).map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1">
            <button onClick={selectAllVisible} className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded text-[hsl(var(--muted-foreground))] hover:text-orange-500 hover:bg-orange-500/10 transition-colors" data-testid="target-select-all" type="button">
              Select visible
            </button>
            <button onClick={clearAllVisible} className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded text-[hsl(var(--muted-foreground))] hover:text-red-500 hover:bg-red-500/10 transition-colors" data-testid="target-clear-all" type="button">
              Clear visible
            </button>
          </div>

          {/* CSV import */}
          <div className="flex items-center gap-1 ml-auto">
            <input ref={csvInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleCsvFile} data-testid="target-csv-input" />
            <button
              type="button"
              onClick={() => csvInputRef.current?.click()}
              className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-orange-500 hover:border-orange-500/40 hover:bg-orange-500/8 transition-colors"
              data-testid="target-csv-import-btn"
              title="Upload CSV (PO Number, Line Number, Part Number) to auto-select matching items"
            >
              <Upload className="w-3 h-3" /> Import CSV
            </button>
          </div>
        </div>

        {/* CSV preview panel — shown before user confirms the import */}
        {csvPreview && (
          <div className="mx-4 my-2 rounded-lg border border-orange-500/30 bg-orange-500/5 overflow-hidden shrink-0" data-testid="csv-preview-panel">
            {/* Header */}
            <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-orange-500/20 bg-orange-500/8">
              <div className="flex items-center gap-2">
                <Upload className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                <span className="text-[11px] font-bold text-orange-600 dark:text-orange-400">
                  CSV Preview — {csvPreview.total} row{csvPreview.total === 1 ? "" : "s"} parsed
                </span>
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">
                  {csvPreview.matchedRows.length} matched
                </span>
                {csvPreview.unmatchedRows.length > 0 && (
                  <span className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold flex items-center gap-0.5">
                    <AlertTriangle className="w-3 h-3" /> {csvPreview.unmatchedRows.length} unmatched
                  </span>
                )}
              </div>
              <button type="button" onClick={() => setCsvPreview(null)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Format guide */}
            <div className="px-3 py-1.5 border-b border-orange-500/15 flex items-start gap-1.5 bg-[hsl(var(--background))]/60">
              <Info className="w-3 h-3 text-orange-400/70 mt-0.5 shrink-0" />
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] leading-snug">
                <span className="font-bold">Expected CSV format:</span> columns{" "}
                <code className="bg-[hsl(var(--muted))]/50 px-0.5 rounded text-[9px]">PO Number</code>,{" "}
                <code className="bg-[hsl(var(--muted))]/50 px-0.5 rounded text-[9px]">Line Number</code>,{" "}
                <code className="bg-[hsl(var(--muted))]/50 px-0.5 rounded text-[9px]">Part Number</code> in any order (column names are case-insensitive). Matching uses PO # + Line # first, or PO # + Part # as fallback.
              </p>
            </div>

            {/* Matched rows (compact, max 6 shown) */}
            {csvPreview.matchedRows.length > 0 && (
              <div className="px-3 py-1.5 space-y-0.5 max-h-[120px] overflow-y-auto">
                <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600/80 mb-1">Matched items</div>
                {csvPreview.matchedRows.slice(0, 8).map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px]">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                    <span className="font-mono-data text-[hsl(var(--muted-foreground))]">{r.csvPo}</span>
                    {r.csvLine && <span className="font-mono-data text-[hsl(var(--muted-foreground))]/60">Ln {r.csvLine}</span>}
                    {r.csvPart && <span className="font-mono-data text-[hsl(var(--muted-foreground))]/60">{r.csvPart}</span>}
                    <span className="text-[hsl(var(--foreground))]/70 truncate ml-auto max-w-[180px]">{r.desc}</span>
                  </div>
                ))}
                {csvPreview.matchedRows.length > 8 && (
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))] italic">+{csvPreview.matchedRows.length - 8} more matched rows…</div>
                )}
              </div>
            )}

            {/* Unmatched rows (collapsed by default) */}
            {csvPreview.unmatchedRows.length > 0 && (
              <div className="border-t border-orange-500/15">
                <button
                  type="button"
                  onClick={() => setShowUnmatched(v => !v)}
                  className="w-full px-3 py-1.5 flex items-center gap-1.5 text-[10px] text-amber-600 dark:text-amber-400 font-semibold hover:bg-amber-500/5 transition-colors"
                  data-testid="csv-toggle-unmatched"
                >
                  <AlertTriangle className="w-3 h-3 shrink-0" />
                  {csvPreview.unmatchedRows.length} row{csvPreview.unmatchedRows.length === 1 ? "" : "s"} couldn't be matched
                  {showUnmatched ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                </button>
                {showUnmatched && (
                  <div className="px-3 pb-1.5 space-y-0.5 max-h-[80px] overflow-y-auto bg-amber-500/5">
                    {csvPreview.unmatchedRows.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-[10px]">
                        <X className="w-3 h-3 text-amber-500 shrink-0" />
                        <span className="font-mono-data text-[hsl(var(--muted-foreground))]">{r.po}</span>
                        <span className="font-mono-data text-[hsl(var(--muted-foreground))]/60">Ln {r.line}</span>
                        <span className="font-mono-data text-[hsl(var(--muted-foreground))]/60">{r.part}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Confirm / dismiss */}
            <div className="px-3 py-2 border-t border-orange-500/20 flex items-center justify-between gap-2 bg-[hsl(var(--background))]/60">
              <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
                {csvPreview.matchedRows.length === 0
                  ? "No items to add — check your CSV file."
                  : `Confirming will add ${csvPreview.matchedRows.length} item${csvPreview.matchedRows.length === 1 ? "" : "s"} to your selection.`}
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setCsvPreview(null)}
                  className="text-[10px] font-bold px-2.5 py-1.5 rounded text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/60 transition-colors"
                  data-testid="csv-preview-dismiss"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={confirmCsvImport}
                  disabled={csvPreview.matchedRows.length === 0}
                  className="text-[10px] font-bold px-3 py-1.5 rounded bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  data-testid="csv-preview-confirm"
                >
                  <CheckCircle2 className="w-3 h-3" /> Add {csvPreview.matchedRows.length} item{csvPreview.matchedRows.length === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Table header ── */}
        <div className="px-4 shrink-0 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
          <div
            className="grid items-center py-2 gap-x-2 text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]"
            style={{ gridTemplateColumns: gridCols }}
          >
            <div />
            <div>PO #</div>
            <div>LN</div>
            <div>PART #</div>
            <div>Description</div>
            <div>Stage</div>
            <div className="text-right">Qty</div>
            <div className="text-right">Due</div>
            {showValue && <div className="text-right">Value</div>}
          </div>
        </div>

        {/* ── Items list ── */}
        <div className="flex-1 overflow-y-auto px-4 py-1.5 min-h-[260px]" data-testid="target-items-list">
          {loading ? (
            <div className="flex items-center justify-center h-48 text-[hsl(var(--muted-foreground))]">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading target…
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-[hsl(var(--muted-foreground))] text-sm">
              <Package className="w-8 h-8 mb-2 opacity-40" />
              No PO lines match. Try a different month or clear filters.
            </div>
          ) : (
            <div className="space-y-0.5">
              {visibleItems.map(item => {
                const checked = selectedIds.has(item.id);
                const cc = clientColor(item.client);
                const sc = STAGE_COLORS[item.stage] || "#94a3b8";
                const due = parseDateDash(item.dispatch_due_date || item.due_date);
                const dueStr = due
                  ? `${String(due.getDate()).padStart(2,"0")} ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][due.getMonth()]}`
                  : "—";
                return (
                  <label
                    key={item.id}
                    htmlFor={`tgt-${item.id}`}
                    className={`grid items-center gap-x-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                      checked
                        ? "bg-orange-500/8 hover:bg-orange-500/12"
                        : "hover:bg-[hsl(var(--muted))]/40"
                    }`}
                    style={{ gridTemplateColumns: gridCols }}
                    data-testid={`target-row-${item.id}`}
                  >
                    <input
                      id={`tgt-${item.id}`}
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(item.id)}
                      className="w-4 h-4 rounded accent-orange-500 cursor-pointer"
                      data-testid={`target-checkbox-${item.id}`}
                    />
                    {/* PO # */}
                    <span className="font-mono-data text-[11px] font-bold text-[hsl(var(--foreground))] truncate">{item.po_number || "—"}</span>
                    {/* Line # */}
                    <span className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))] truncate">{item.line_number || "—"}</span>
                    {/* Part # */}
                    <span className="font-mono-data text-[11px] text-[hsl(var(--foreground))]/80 truncate">{item.part_number || "—"}</span>
                    {/* Description */}
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-[11px] font-bold px-1 py-0.5 rounded shrink-0" style={{ color: cc, background: cc + "18" }}>{item.client}</span>
                      <span className="text-xs text-[hsl(var(--foreground))]/80 truncate">{item.item_description}</span>
                    </div>
                    {/* Stage */}
                    <span
                      className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-center truncate"
                      style={{ color: sc, background: sc + "1e", border: `1px solid ${sc}38` }}
                    >
                      {item.stage}
                    </span>
                    {/* Qty */}
                    <span className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))] text-right">{item.quantity}</span>
                    {/* Due */}
                    <span className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))] text-right">{dueStr}</span>
                    {/* Value */}
                    {showValue && (
                      <span className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))] text-right">{formatCurrency(item.value_usd || 0)}</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>
        </>) } {/* end po_lines mode */}

        {/* ── Footer ── */}
        <div className="px-6 py-3.5 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 flex items-center justify-between gap-3 shrink-0">
          {/* Left — selection counter + reset */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              {targetType === "race" ? (
                <>
                  <Hash className="w-4 h-4 text-orange-500" />
                  <span className="text-2xl font-bold font-mono-data tracking-tight text-orange-600 dark:text-orange-400" data-testid="target-selected-count">{raceTarget}</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">items target</span>
                </>
              ) : (
                <>
                  <span className="text-2xl font-bold font-mono-data tracking-tight" data-testid="target-selected-count">{selectedIds.size}</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">line{selectedIds.size === 1 ? "" : "s"} in target</span>
                </>
              )}
            </div>
            {showValue && selectedIds.size > 0 && (
              <>
                <span className="text-[hsl(var(--muted-foreground))]">·</span>
                <span className="font-mono-data text-sm font-medium">{formatCurrency(totalValue)}</span>
              </>
            )}

            {/* Reset Target */}
            {!confirmReset ? (
              <button
                type="button"
                onClick={() => setConfirmReset(true)}
                disabled={saving || resetting || (targetType === "po_lines" && selectedIds.size === 0)}
                className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded border border-red-500/30 text-red-500/70 hover:text-red-500 hover:bg-red-500/10 hover:border-red-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="target-reset-btn"
              >
                <Trash2 className="w-3 h-3" /> Reset target
              </button>
            ) : (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-red-500 font-semibold">Clear all {selectedIds.size} items?</span>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={resetting}
                  className="text-[10px] font-bold px-2.5 py-1.5 rounded bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors flex items-center gap-1"
                  data-testid="target-reset-confirm-btn"
                >
                  {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Confirm reset
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmReset(false)}
                  className="text-[10px] font-bold px-2.5 py-1.5 rounded text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/60 transition-colors"
                  data-testid="target-reset-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Right — Cancel / Save */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="text-xs font-semibold px-3 py-2 rounded-md text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/60 transition-colors"
              type="button"
              data-testid="target-cancel-btn"
            >
              <X className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-xs font-bold px-4 py-2 rounded-md bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              type="button"
              data-testid="target-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Save target
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
